import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

import { Faq } from "@/components/site/Faq";
import { scheduleUrl } from "@/lib/site-data";
import scottsdale__asset from "@/assets/restore/locations/scottsdale.jpg.asset.json";
import harrisConsult__asset from "@/assets/restore/harris-consult.webp.asset.json";

const scottsdale = scottsdale__asset.url;
const harrisConsult = harrisConsult__asset.url;
const PHONE = "(866) 488-9356";

type City = {
  name: string;
  address: string;
  img: string;
  mapUrl: string;
  to: "/locations/arizona/scottsdale";
};

const cities: City[] = [
  {
    name: "Scottsdale",
    address: "7150 East Camelback Rd, Suite 444",
    img: scottsdale,
    mapUrl: "https://www.google.com/maps/search/?api=1&query=7150+East+Camelback+Rd+Scottsdale+AZ",
    to: "/locations/arizona/scottsdale",
  },
];

export const Route = createFileRoute("/locations/arizona/")({
  head: () => ({
    meta: [
      { title: "Arizona Hair Transplants | RESTORE® Hair" },
      {
        name: "description",
        content:
          "RESTORE® Hair in Arizona. Find our Scottsdale location for a free, personal hair loss consultation and learn about your FUE options.",
      },
      { property: "og:title", content: "Arizona Hair Transplants | RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "See RESTORE® Hair in Arizona and book a free consultation in Scottsdale.",
      },
      { property: "og:url", content: "https://restorehair.com/locations/arizona/" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/arizona/" }],
  }),
  component: ArizonaHub,
});

function ArizonaHub() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-background py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
            <p className="text-sm font-semibold tracking-wide text-foreground">
              Arizona Hair Transplants
            </p>
            <h1 className="mt-3 text-2xl font-extrabold leading-tight text-foreground md:text-3xl">
              <span className="font-extrabold">RESTORE®</span>{" "}
              <span className="font-light">Hair in Arizona</span>
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-base text-foreground/80 md:text-lg">
              For nearly 20 years, RESTORE® has changed the lives of more than 20,000 men thanks
              to this affordable, life-changing FUE hair transplant procedure. Regardless of your
              hair's nature or the extent of hair loss, our dedicated team is committed to
              delivering results that will undoubtedly delight you. If you are suffering from
              hair loss and live in Arizona, check out our locations below to get a FREE personal
              hair loss consultation and learn about your options on how to address your hair loss
              with RESTORE.
            </p>
            <p className="mt-4 text-base text-foreground/80">
              In the Phoenix area?{" "}
              <a href="/locations/arizona/phoenix/" className="font-semibold text-primary underline underline-offset-4">
                Hair transplants in Phoenix
              </a>
            </p>
          </div>
        </section>

        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center text-xl font-extrabold leading-tight text-foreground md:text-2xl">
              Hair Transplant Locations in Arizona
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-center text-base text-foreground/80">
              Embarking on the transformative journey of a hair consultation in Arizona is made
              more convenient when choosing a local neighborhood that aligns with your lifestyle.
              See the local cities in Arizona we serve!
            </p>

            <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-6">
              {cities.map((loc) => (
                <article
                  key={loc.name}
                  className="grid grid-cols-1 overflow-hidden border border-border bg-background shadow-sm sm:grid-cols-[1fr_260px]"
                >
                  <div className="flex flex-col p-6">
                    <h3 className="border-b-2 border-primary pb-1 text-2xl font-bold text-foreground inline-block self-start">
                      {loc.name}
                    </h3>
                    <div className="mt-4 text-sm text-foreground/80">
                      <p>{loc.address}</p>
                      <p className="mt-1">{PHONE}</p>
                    </div>
                    <a
                      href={loc.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 text-sm font-bold text-primary underline underline-offset-4 hover:no-underline self-start"
                    >
                      View Map
                    </a>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <a
                        href={scheduleUrl}
                        className="inline-block bg-primary px-5 py-3 text-center text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90"
                      >
                        Book a Virtual Consultation
                      </a>
                      <a
                        href={`${loc.to}/`}
                        className="inline-block border border-foreground/20 px-5 py-3 text-center text-sm font-bold tracking-wider text-foreground transition hover:bg-foreground/5"
                      >
                        Explore Hair Restoration in {loc.name}
                      </a>
                    </div>
                  </div>
                  <img
                    src={loc.img}
                    alt={`RESTORE Hair ${loc.name} Arizona location`}
                    loading="lazy"
                    className="h-56 w-full object-cover sm:h-full"
                  />
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="overflow-hidden">
          <div className="mx-auto grid max-w-[1280px] grid-cols-1 lg:h-[644px] lg:grid-cols-2">
            <img
              src={harrisConsult}
              alt="Dr. James Harris consulting with a RESTORE patient"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div
              className="flex flex-col justify-center px-10 py-10 text-white lg:px-12 lg:py-12"
              style={{ backgroundColor: "#5c5c5c" }}
            >
              <h2 className="text-[30px] font-bold leading-tight">
                Schedule Your Complimentary Virtual Hair Consultation Today
              </h2>
              <p className="mt-3 text-lg font-semibold">
                We've made the Hair Restoration Process Even Easier!
              </p>
              <p className="mt-3 max-w-md text-base leading-relaxed text-white/90">
                From the comfort of your home, we will explain how the FUE procedure works and
                create a customized hair loss treatment plan, tailored for you specific needs and
                amount of hair loss.
              </p>
              <a
                href={scheduleUrl}
                className="mt-6 inline-block w-fit bg-primary px-8 py-4 text-center text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90"
              >
                Schedule Now
              </a>
              <p className="mt-4 max-w-md text-sm italic leading-relaxed text-white/80">
                Restoration Holdings LLC is a healthcare management company focused exclusively on
                hair restoration solutions across the United States.
              </p>
            </div>
          </div>
        </section>

        <Faq />
      </main>
      <Footer />
    </div>
  );
}